// Constants for metric key patterns
const STEP_METRICS_PATTERNS = {
  CLAIMS: /^step_\d+_claims$/,
  PATIENTS: /^step_\d+_patients$/,
} as const;

/**
 * Location data structure for metrics with location information
 */
export interface MetricLocation {
  type?: string;
  provider_id?: string;
  name?: string;
  claims?: number;
}

/**
 * Step metrics structure supporting both locations and direct metric values
 */
export interface StepMetrics {
  locations?: MetricLocation[];
  [key: string]: any; // Dynamic step_N_claims or step_N_patients keys
}

/**
 * Provider details structure with metrics data
 */
export interface ProviderMetricsData {
  metrics_v2?: {
    [stepId: number]: StepMetrics;
  };
  metrics?: {
    claims?: number;
  };
}

/**
 * Extracts metric value for a given step from provider data.
 *
 * Handles multiple metric formats:
 * 1. Location-based metrics (summed claims from all locations)
 * 2. Step-specific claims (step_N_claims pattern)
 * 3. Step-specific patients (step_N_patients pattern)
 * 4. Legacy metrics format (fallback)
 *
 * @param stepId - The step ID to retrieve metrics for
 * @param selectedProvider - Provider data containing metrics
 * @returns The metric value or '-' if unavailable
 *
 * @example
 * ```typescript
 * const value = getMetricValue(1, providerData);
 * // Returns: 42 or '-'
 * ```
 */
export function getMetricValue(
  stepId: number,
  selectedProvider?: ProviderMetricsData
): string | number {
  if (!selectedProvider) {
    return '-';
  }

  if (selectedProvider.metrics_v2 && selectedProvider.metrics_v2[stepId]) {
    const stepMetrics = selectedProvider.metrics_v2[stepId];

    // Handle locations if present (takes priority)
    if (
      Array.isArray(stepMetrics.locations) &&
      stepMetrics.locations.length > 0
    ) {
      // Sum up claims from all locations
      const totalClaims = stepMetrics.locations.reduce(
        (sum: number, loc: MetricLocation) => sum + (loc.claims || 0),
        0
      );
      return totalClaims > 0 ? totalClaims : '-';
    }

    // Find step_N_claims or step_N_patients keys dynamically
    // Steps can be non-consecutive (e.g., step_0, step_2, step_5) due to deletions
    const keys = Object.keys(stepMetrics);

    // Look for step_N_claims pattern
    const claimsKey = keys.find((key) =>
      STEP_METRICS_PATTERNS.CLAIMS.test(key)
    );
    if (claimsKey && stepMetrics[claimsKey] !== undefined) {
      return stepMetrics[claimsKey];
    }

    // Look for step_N_patients pattern as fallback
    const patientsKey = keys.find((key) =>
      STEP_METRICS_PATTERNS.PATIENTS.test(key)
    );
    if (patientsKey && stepMetrics[patientsKey] !== undefined) {
      return stepMetrics[patientsKey];
    }
  }

  // Fallback to legacy metrics format
  if (selectedProvider.metrics?.claims !== undefined) {
    return selectedProvider.metrics.claims;
  }

  return '-';
}
